'use client';

import { c as _c } from "react/compiler-runtime";
import { FactiiiRequirement } from '@shared/all/domains/factiiis';
import { type RouterOutputs } from '@shared/all/domains/trpc';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import useNavigator from '@shared/utils/hooks/useNavigator';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield, Users } from 'lucide-react';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { factiiiScope, useRegisterSearchScope } from '../../hooks/useSearchScope';
import AppLayout from '../../layouts/AppLayout/AppLayout';
import { client } from '../../utils/trpc';
import NotFound from '../common/NotFound';
import FactiiiHeader from '../factiiis/FactiiiHeader';
import GoBackButton from '../factiiis/general/GoBackButton';
import MediaMatchGroups from '../factiiis/MediaMatchGroups';
import { RelatedFactiiisSidebar } from '../factiiis/RelatedFactiiisSidebar';
import Posts from '../posts/Posts';
import Rules from '../spaces/Rules';
type PageType = 'direct' | 'space' | 'user';
interface UnifiedFactiiiPageProps {
  pageType: PageType;
  initialData?: RouterOutputs['factiiis']['factiiiItems'];
  initialError?: boolean;
  isEmbed: boolean;
}
const UnifiedFactiiiPage = t0 => {
  const $ = _c(66);
  const {
    pageType,
    initialData,
    initialError,
    isEmbed
  } = t0;
  const {
    setBackNavigation,
    navigatorState
  } = useNavigator();
  const queryClient = useQueryClient();
  const {
    localPreferenceState
  } = useLocalPreference();
  const params = useParams();
  const {
    slug,
    spaceSlug,
    username,
    ids
  } = params as {
    slug?: string;
    spaceSlug?: string;
    username?: string;
    ids?: string;
  };
  const [backNavigationSet, setBackNavigationSet] = useState(false);
  let t1;
  if ($[0] !== ids || $[1] !== pageType || $[2] !== slug || $[3] !== spaceSlug || $[4] !== username) {
    t1 = ["factiiiData", pageType, slug, spaceSlug, username, ids];
    $[0] = ids;
    $[1] = pageType;
    $[2] = slug;
    $[3] = spaceSlug;
    $[4] = username;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const queryKey = t1;
  let t2;
  if ($[6] !== ids || $[7] !== pageType || $[8] !== slug || $[9] !== spaceSlug || $[10] !== username) {
    t2 = async () => {
      if (pageType === "direct") {
        if (!ids) {
          return {
            factiiiList: []
          };
        }
        const decodedIds = decodeURIComponent(ids);
        const factiiiIds = decodedIds.split(",").map(_temp).filter(_temp2);
        if (!factiiiIds || factiiiIds.length === 0) {
          return {
            factiiiList: []
          };
        }
        return await unwrapFromResult(client.factiiis.factiiiItems.query({
          factiiiIds
        }));
      } else {
        return await unwrapFromResult(client.factiiis.factiiiItems.query({
          factiiiSlug: slug,
          spaceSlug,
          username
        }));
      }
    };
    $[6] = ids;
    $[7] = pageType;
    $[8] = slug;
    $[9] = spaceSlug;
    $[10] = username;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = (pageType === "direct" && Boolean(ids) || pageType !== "direct" && Boolean(slug)) && !initialError;
  let t4;
  if ($[12] !== initialData || $[13] !== queryKey || $[14] !== t2 || $[15] !== t3) {
    t4 = {
      queryKey,
      queryFn: t2,
      enabled: t3,
      initialData,
      staleTime: 300000
    };
    $[12] = initialData;
    $[13] = queryKey;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const {
    data: factiiiData,
    isLoading
  } = useQuery(t4);
  const factiiiList = factiiiData?.factiiiList || [];
  const [activeFactiiiId, setActiveFactiiiId] = useState(factiiiList[0]?.id || null);
  const activeFactiii = factiiiList?.find(f => f.id === activeFactiiiId);
  useEffect(() => {
    if (factiiiList.length > 0) {
      setActiveFactiiiId(factiiiList[0].id);
    }
  }, [factiiiList]);
  useRegisterSearchScope(activeFactiii ? factiiiScope(activeFactiii.id, activeFactiii.slug) : null);
  let t5;
  if ($[17] !== activeFactiii || $[18] !== navigatorState || $[19] !== setBackNavigation) {
    t5 = () => {
      if (!activeFactiii) {
        return;
      }
      const factiii = activeFactiii;
      if (!factiii) {
        return;
      }
      if (navigatorState && !navigatorState.backNavigation) {
        if (factiii.spaceSlug) {
          setBackNavigation({
            path: `/s/${factiii.spaceSlug}`,
            context: `Go back to ${factiii.spaceSlug}'s space`
          });
        } else {
          if (factiii.username) {
            setBackNavigation({
              path: `/user/${factiii.username}`,
              context: `Go to ${factiii.username}'s profile`
            });
          }
        }
        setBackNavigationSet(true);
      }
    };
    $[17] = activeFactiii;
    $[18] = navigatorState;
    $[19] = setBackNavigation;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== activeFactiii || $[22] !== navigatorState.backNavigation || $[23] !== pageType) {
    t6 = [activeFactiii, navigatorState.backNavigation, pageType];
    $[21] = activeFactiii;
    $[22] = navigatorState.backNavigation;
    $[23] = pageType;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  useEffect(t5, t6);
  if (isLoading) {
    let t7;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="flex min-h-[400px] items-center justify-center"><div className="loader" /></div>;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    let t8;
    if ($[26] !== isEmbed) {
      t8 = <AppLayout isEmbed={isEmbed}>{t7}</AppLayout>;
      $[26] = isEmbed;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    return t8;
  }
  if (factiiiList.length === 0) {
    if (pageType === "direct" && ids) {
      const decodedIds_0 = decodeURIComponent(ids);
      const factiiiIds_0 = decodedIds_0.split(",").map(_temp3).filter(_temp4);
      if (factiiiIds_0.length === 0) {
        const t7 = decodeURIComponent(ids);
        let t8;
        if ($[28] !== t7) {
          t8 = <p className="text-textSecondary">Invalid factiii IDs: "{t7}"</p>;
          $[28] = t7;
          $[29] = t8;
        } else {
          t8 = $[29];
        }
        let t9;
        if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <p className="mt-2 text-sm text-textSecondary">Please provide valid comma-separated factiii IDs (e.g., 123,456,789)</p>;
          $[30] = t9;
        } else {
          t9 = $[30];
        }
        let t10;
        if ($[31] !== t8) {
          t10 = <div className="py-8 text-center">{t8}{t9}</div>;
          $[31] = t8;
          $[32] = t10;
        } else {
          t10 = $[32];
        }
        let t11;
        if ($[33] !== isEmbed || $[34] !== t10) {
          t11 = <AppLayout isEmbed={isEmbed}>{t10}</AppLayout>;
          $[33] = isEmbed;
          $[34] = t10;
          $[35] = t11;
        } else {
          t11 = $[35];
        }
        return t11;
      }
    }
    let t7;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <NotFound item="factiii" />;
      $[36] = t7;
    } else {
      t7 = $[36];
    }
    let t8;
    if ($[37] !== isEmbed) {
      t8 = <AppLayout isEmbed={isEmbed}>{t7}</AppLayout>;
      $[37] = isEmbed;
      $[38] = t8;
    } else {
      t8 = $[38];
    }
    return t8;
  }
  const currentFactiii = activeFactiii;
  const allFactiiis = factiiiList;
  let t7;
  if ($[39] !== allFactiiis || $[40] !== currentFactiii || $[41] !== isLoading || $[42] !== setActiveFactiiiId) {
    t7 = !isLoading && currentFactiii && <FactiiiHeader factiii={currentFactiii} allFactiiis={allFactiiis} onFactiiiSelect={setActiveFactiiiId} />;
    $[39] = allFactiiis;
    $[40] = currentFactiii;
    $[41] = isLoading;
    $[42] = setActiveFactiiiId;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  const t8 = !isEmbed;
  let t9;
  if ($[44] !== currentFactiii || $[45] !== queryClient || $[46] !== queryKey) {
    t9 = {
      items: currentFactiii ? [{
        icon: <Shield className="size-5" />,
        text: "Rules",
        component: <Rules addRule={rule => {
          queryClient.setQueryData(queryKey, old => {
            if (!old) {
              return old;
            }
            return {
              ...old,
              factiiiList: old.factiiiList.map(f_0 => f_0.id === currentFactiii.id ? {
                ...f_0,
                rules: [...f_0.rules, rule]
              } : f_0)
            };
          });
        }} editRule={rule_0 => {
          queryClient.setQueryData(queryKey, old_0 => {
            if (!old_0) {
              return old_0;
            }
            return {
              ...old_0,
              factiiiList: old_0.factiiiList.map(f_1 => f_1.id === currentFactiii.id ? {
                ...f_1,
                rules: f_1.rules.map(r => r.id === rule_0.id ? rule_0 : r)
              } : f_1)
            };
          });
        }} factiii={{
          rules: currentFactiii.rules,
          owner: currentFactiii.owner,
          id: currentFactiii.id
        }} />
      }] : []
    };
    $[44] = currentFactiii;
    $[45] = queryClient;
    $[46] = queryKey;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  let t10;
  if ($[48] !== currentFactiii) {
    t10 = {
      items: currentFactiii ? [{
        icon: <Users className="size-5" />,
        text: "Related Factiiis",
        component: <RelatedFactiiisSidebar factiiiId={currentFactiii.id} />
      }] : []
    };
    $[48] = currentFactiii;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] !== allFactiiis || $[51] !== backNavigationSet || $[52] !== currentFactiii || $[53] !== isLoading || $[54] !== localPreferenceState) {
    t11 = !isLoading && currentFactiii && <>{backNavigationSet && <GoBackButton />}{currentFactiii.requirements.includes(FactiiiRequirement.MEDIA_MATCH) && !localPreferenceState.mediaSearch ? <div data-id="factiii-posts"><MediaMatchGroups factiiiIds={allFactiiis.map(_temp5)} /></div> : (localPreferenceState.postFilterType || localPreferenceState.mediaSearch) && <div data-id="factiii-posts"><Posts params={{
          source: "factiiis",
          factiiiIds: allFactiiis.map(_temp6)
        }} disableLink={false} isFullLength={false} /></div>}</>;
    $[50] = allFactiiis;
    $[51] = backNavigationSet;
    $[52] = currentFactiii;
    $[53] = isLoading;
    $[54] = localPreferenceState;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  let t12;
  if ($[56] !== t11) {
    t12 = <div className="flex flex-col space-y-2">{t11}</div>;
    $[56] = t11;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  let t13;
  if ($[58] !== currentFactiii || $[59] !== isEmbed || $[60] !== t10 || $[61] !== t12 || $[62] !== t7 || $[63] !== t8 || $[64] !== t9) {
    t13 = <AppLayout topBanner={t7} showPostRenderOptions={t8} pinnedFactiii={currentFactiii} isEmbed={isEmbed} leftSidebar={t9} rightSidebar={t10}>{t12}</AppLayout>;
    $[58] = currentFactiii;
    $[59] = isEmbed;
    $[60] = t10;
    $[61] = t12;
    $[62] = t7;
    $[63] = t8;
    $[64] = t9;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  return t13;
};
export default UnifiedFactiiiPage;
function _temp(id) {
  return Number(id.trim());
}
function _temp2(id_0) {
  return !isNaN(id_0) && id_0 > 0;
}
function _temp3(id_1) {
  return Number(id_1.trim());
}
function _temp4(id_2) {
  return !isNaN(id_2) && id_2 > 0;
}
function _temp5(f_2) {
  return f_2.id;
}
function _temp6(f_3) {
  return f_3.id;
}